import React from 'react';
import { Campaign } from '../../types/Campaign';
import { TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status badge styling
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-1760">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <table className="w-full text-left border-collapse" data-id="element-1761">
      <thead data-id="element-1762">
        <tr className="border-b border-gray-100" data-id="element-1763">
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1764">
            Campaign
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1765">
            Code Strategy
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1766">
            Discount
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1767">
            Budget Usage
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1768">
            Expiry Date
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-1769">
            Status
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100" data-id="element-1770">
        {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} onClick={() => onCampaignClick(campaign.id)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-1771">
              <td className="px-6 py-4" data-id="element-1772">
                <div className="text-sm font-medium text-gray-900 group-hover:text-[#F7941D] transition-colors" data-id="element-1773">
                  {campaign.title}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-1774">
                  {campaign.category}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-1775">
                <div className="text-sm text-gray-900" data-id="element-1776">
                  {campaign.codeStrategy === 'Global' ? <span className="font-medium" data-id="element-1777">{campaign.code}</span> : <span data-id="element-1778">Generated Codes</span>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-1779">
                  {campaign.codeStrategy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-1780">
                <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-1781">
                  {campaign.discountPercentage > 0 ? <>
                      <PercentIcon size={14} className="mr-1 text-gray-400" data-id="element-1782" />
                      {campaign.discountPercentage}%
                    </> : <>
                      <DollarSignIcon size={14} className="mr-1 text-gray-400" data-id="element-1783" />
                      {campaign.fixedDiscountAmount} {campaign.currency}
                    </>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-1784">
                  Carried by {campaign.discountCarriedBy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-1785">
                <div className="flex items-center gap-2" data-id="element-1786">
                  <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-1787">
                    <div className="bg-[#F7941D] h-1.5 rounded-full transition-all" style={{
                width: `${calculateBudgetUsage(campaign)}%`
              }} data-id="element-1788"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1789">
                    {calculateBudgetUsage(campaign)}%
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-1790">
                  {campaign.budgetUsed.toLocaleString()} /{' '}
                  {campaign.budget.toLocaleString()} {campaign.currency}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-1791">
                <div className="text-sm text-gray-900" data-id="element-1792">
                  {campaign.expiryDate}
                </div>
              </td>
              <td className="px-6 py-4 text-right" data-id="element-1793">
                {getStatusBadge(campaign.status)}
              </td>
            </tr>) : <tr data-id="element-1794">
            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-1795">
              No campaigns found matching your criteria.
            </td>
          </tr>}
      </tbody>
    </table>;
}